/**
 * The AppFrame layout table (adr-0023). The ONLY place side-panel widths and
 * docking breakpoints are decided, for every app.
 *
 * Apps declare what a side is — `essential` or `supplementary`, `list` or
 * `structured` content — and which side gives way first. Everything numeric
 * follows from that and from one rule: a side docks once the main pane would
 * still keep `MAIN_MIN` with it docked. Retune here; never per app.
 *
 * Pure, so the thresholds are tested without a DOM.
 */

export type Side = "left" | "right";
export type SideRole = "essential" | "supplementary";
export type SideContent = "list" | "structured";
/** Which side gives way first as the viewport narrows. */
export type YieldSide = Side | "together";

/** The main pane never gets narrower than this while a side is docked. */
export const MAIN_MIN = 512;

/** Width follows from content: a flat list, or something with its own structure. */
export const SIDE_WIDTH: Record<SideContent, number> = {
  list: 256,
  structured: 368,
};

/**
 * Below this, a drawer takes most of the screen (`DRAWER_PHONE_WIDTH`),
 * leaving a finger-width strip of main pane to tap to dismiss. From it up, a
 * drawer is the side's own width — on a tablet, 85vw would cover the content.
 */
export const DRAWER_FULL_FROM = 640;
export const DRAWER_PHONE_WIDTH = "85vw";

export interface SideSpec {
  role: SideRole;
  content: SideContent;
}

/**
 * The viewport width at which each side docks, or `null` for a side that is
 * not there.
 *
 * The side that does not yield is PRIMARY and is placed first: it docks at
 * `MAIN_MIN + its width`. The secondary docks at `MAIN_MIN + both widths` —
 * or, when the primary is absent (a right panel with nothing in it), as if it
 * were primary. `together` docks both at the same point, and neither alone.
 */
export function dockThresholds(
  yieldSide: YieldSide,
  sides: { left?: SideSpec; right?: SideSpec },
): { left: number | null; right: number | null } {
  const w = (s: Side) => (sides[s] ? SIDE_WIDTH[sides[s]!.content] : 0);

  if (yieldSide === "together") {
    const both = MAIN_MIN + w("left") + w("right");
    return {
      left: sides.left ? both : null,
      right: sides.right ? both : null,
    };
  }

  const primary: Side = yieldSide === "left" ? "right" : "left";
  const secondary: Side = yieldSide;
  const out: { left: number | null; right: number | null } = { left: null, right: null };
  if (sides[primary]) out[primary] = MAIN_MIN + w(primary);
  if (sides[secondary]) out[secondary] = MAIN_MIN + w(primary) + w(secondary);
  return out;
}

/** Whether a docked side starts open before the user has chosen. */
export function openByDefault(role: SideRole): boolean {
  // Essential content (an app's navigation) is shown; supplementary content
  // is one click away and does not take width until asked for.
  return role === "essential";
}
